import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { formatApiError } from "../api";
import { useT } from "../i18n";
import Header from "../components/Header";
import AvailabilitySection from "../components/AvailabilitySection";

const inputCls = "w-full min-h-[48px] rounded-xl border border-[#0D4045]/15 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D4045]/30";
const LANG_OPTIONS = [["fr", "Français"], ["gcf", "Kreyòl"], ["en", "English"]];

export default function ProDashboard() {
  const { t } = useT();
  const [pro, setPro] = useState(null);
  const [meta, setMeta] = useState({ specialties: [], communes: [], stale_days: 30 });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/pro/me").then((res) => {
    setPro(res.data);
    setForm({
      name: res.data.name, specialty: res.data.specialty, commune: res.data.commune,
      phone: res.data.phone || "", contact_email: res.data.contact_email || "",
      note: res.data.note || "", languages: res.data.languages || ["fr"],
      places_total: res.data.places_total ?? null,
      places_available: res.data.places_available ?? null,
      wait_estimate: res.data.wait_estimate || "",
    });
  }).catch((err) => toast.error(formatApiError(err))), []);

  useEffect(() => {
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
    load();
  }, [load]);

  const setStatus = async (status) => {
    try {
      const res = await api.post("/pro/me/availability", { status });
      setPro(res.data);
      toast.success(t("pro.status.updated"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put("/pro/me", form);
      setPro(res.data);
      toast.success(t("pro.profile.saved"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!pro || !form) {
    return <div className="min-h-screen"><Header /><div className="py-20 text-center text-[#0D4045] font-semibold">…</div></div>;
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-10 flex flex-col gap-8">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#1F2937]" style={{ fontFamily: "Manrope" }}>{t("pro.title")}</h1>
          {!pro.approved && (
            <p data-testid="pro-pending-notice" className="mt-3 text-sm font-semibold text-[#92400E] bg-[#FEF3C7] border border-[#FBBF24]/60 rounded-xl px-4 py-3">
              {t("pro.pending")}
            </p>
          )}
        </div>

        <AvailabilitySection pro={pro} staleDays={meta.stale_days} onStatus={setStatus} />

        <section className="bg-white rounded-xl border border-black/5 p-6" data-testid="profile-section">
          <h2 className="text-lg font-bold text-[#1F2937]" style={{ fontFamily: "Manrope" }}>{t("pro.profile.title")}</h2>
          <form onSubmit={saveProfile} className="flex flex-col gap-4 mt-5">
            <input data-testid="profile-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("login.name")} className={inputCls} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select data-testid="profile-specialty-select" value={form.specialty}
                onChange={(e) => setForm({ ...form, specialty: e.target.value })} className={inputCls}>
                {meta.specialties.map((s) => <option key={s} value={s}>{t(`specialty.${s}`)}</option>)}
              </select>
              <select data-testid="profile-commune-select" value={form.commune}
                onChange={(e) => setForm({ ...form, commune: e.target.value })} className={inputCls}>
                {meta.communes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="profile-phone-input" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("login.phone")} className={inputCls} />
              <input data-testid="profile-email-input" type="email" value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder={t("login.email")} className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input data-testid="profile-places-available-input" type="number" min="0"
                value={form.places_available ?? ""} placeholder={t("pro.places")}
                onChange={(e) => setForm({ ...form, places_available: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                className={inputCls} />
              <input data-testid="profile-places-total-input" type="number" min="0"
                value={form.places_total ?? ""} placeholder={t("pro.places.total")}
                onChange={(e) => setForm({ ...form, places_total: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                className={inputCls} />
              <input data-testid="profile-wait-input" value={form.wait_estimate}
                placeholder={t("pro.wait")}
                onChange={(e) => setForm({ ...form, wait_estimate: e.target.value })}
                className={inputCls} />
            </div>
            <div className="flex gap-3" data-testid="profile-languages">
              {LANG_OPTIONS.map(([code, label]) => (
                <label key={code} className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold cursor-pointer transition-colors duration-200 ${
                  form.languages.includes(code) ? "bg-[#E8F3F1] border-[#0D4045]/40 text-[#0D4045]" : "bg-white border-[#0D4045]/15 text-[#6B7280]"
                }`}>
                  <input type="checkbox" className="accent-[#0D4045]" data-testid={`profile-lang-${code}`}
                    checked={form.languages.includes(code)}
                    onChange={(e) => setForm({
                      ...form,
                      languages: e.target.checked
                        ? [...form.languages, code]
                        : form.languages.filter((l) => l !== code),
                    })} />
                  {label}
                </label>
              ))}
            </div>
            <textarea data-testid="profile-note-input" rows={3} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("pro.profile.note")}
              className="w-full rounded-xl border border-[#0D4045]/15 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D4045]/30" />
            <button type="submit" disabled={saving} data-testid="profile-save-btn"
              className="flex items-center justify-center gap-2 min-h-[48px] bg-[#0D4045] text-[#FDFBF7] font-semibold rounded-full hover:bg-[#0a3237] transition-colors duration-200 disabled:opacity-60">
              <Save size={16} /> {saving ? "…" : t("pro.profile.save")}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
